// What the document screen hands down to the panels that open inside it.
//
// History and Research are routes under /d/:id rather than states inside it
// (design §14.4), so that going back from one lands on the document and the
// device's own back gesture agrees. The point of nesting them rather than
// putting them beside the document is that /d/:id stays matched the whole
// time: the store, the outbox, the event stream and everything the advisor
// has said survive a visit to a panel, because the component that owns them
// is never unmounted.
//
// Everything a panel needs therefore travels through the outlet rather than
// being fetched again. The one exception is per-document usage, which the
// Research route asks for itself — it is the only reader, and its own mount
// is a better guard than a boolean ever was.

import { useOutletContext } from "react-router-dom";

import type { Me } from "../api/client";
import type { Comment } from "../advisor/types";
import type { Advisor } from "../advisor/useAdvisor";
import type { useFind } from "../editor/useFind";
import type { DocumentStore } from "../store/documentStore";
import type { DocState, ParagraphId } from "../store/ops";
import type { TextSelection } from "../editor/Editor";

export interface DocumentContext {
  documentId: string;
  doc: DocState;
  store: DocumentStore;
  advisor: Advisor;
  advisorBump: number;
  me: Me;

  /** The editor's surface, for the body route. */
  liveSelection: ParagraphId[];
  onSelectionChange: (ids: ParagraphId[]) => void;
  onTextSelection: (selection: TextSelection | null) => void;
  clearText: number;
  find: ReturnType<typeof useFind>;
  trays: Map<ParagraphId, Comment[]>;
  trayCounts: Map<ParagraphId, number>;
  docWide: Comment[];
  openTray: ParagraphId | null;
  onToggleTray: (id: ParagraphId) => void;
  renderCard: (comment: Comment) => React.ReactNode;
  onDismissEvaluation: () => void;

  /** What the panels do back to the document. */
  onDismissNote: (slug: string) => void;
  onGoToParagraph: (id: ParagraphId) => void;
  onRestored: () => void;
  onNotice: (message: string) => void;
  /** A paragraph the body should scroll to as soon as it is mounted again,
   *  set by a panel on its way out. A ref rather than router state, which
   *  would survive back and forward and fire the scroll a second time. */
  pendingScroll: React.RefObject<ParagraphId | null>;
}

export function useDocument(): DocumentContext {
  return useOutletContext<DocumentContext>();
}
